import { ApolloError, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";

import {
  LearningCoursePayloadDocument,
  LearningCoursePayloadQuery,
  LearningCoursePayloadQueryVariables,
  MyLearningCatalogDocument,
  MyLearningCatalogQuery,
  MyLearningCatalogQueryVariables,
  MyLearningDocument,
  MyLearningQuery,
  MyLearningQueryVariables,
} from "../generated/graphql";
import { LearningCourse, parseLearningCoursePayload } from "../lib/learning/contentContract";

export type LearningCourseSummary = {
  id: string;
  version: string;
  locale: string;
  title: string;
  description: string;
  thumbnailFilename: string;
  estimatedDurationMinutes: number;
  passingScorePercent: number;
  tags: string[];
  directAccess: boolean;
  pathAccess: boolean;
  locked: boolean;
};

export type LearningPathSummary = {
  id: string;
  version: string;
  locale: string;
  title: string;
  description: string;
  thumbnailFilename: string;
  enforceOrder: boolean;
  courses: Array<{ courseId: string; position: number }>;
};

export type LearningCatalogState = {
  courses: LearningCourseSummary[];
  learningPaths: LearningPathSummary[];
  loading: boolean;
  error?: ApolloError;
  reload: () => Promise<void>;
};

export const useLearningCatalog = (): LearningCatalogState => {
  const { data, loading, error, refetch } = useQuery<MyLearningCatalogQuery, MyLearningCatalogQueryVariables>(
    MyLearningCatalogDocument,
    {
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    }
  );

  const catalog = useMemo(() => {
    const accessByCourse = new Map(
      (data?.myLearningCatalog.courseAccess ?? []).map(access => [access.courseId, access])
    );
    const courses = (data?.myLearningCatalog.courses ?? [])
      .map(course => {
        const access = accessByCourse.get(course.id);
        const enrolled = Boolean(access && access.currentVersion === course.version);
        return {
          ...course,
          directAccess: enrolled && Boolean(access?.directAccess || access?.selfAccess),
          pathAccess: enrolled && Boolean(access?.pathAccess),
          // Without an access source the course is not assigned; it stays
          // locked and the catalog views do not render it.
          locked: enrolled ? Boolean(access?.locked) : true,
        };
      })
      .sort((left, right) => left.title.localeCompare(right.title, "de-DE"));
    const learningPaths = [...(data?.myLearningCatalog.learningPaths ?? [])].sort((left, right) =>
      left.title.localeCompare(right.title, "de-DE")
    );
    return { courses, learningPaths };
  }, [data]);

  return {
    ...catalog,
    loading,
    error,
    reload: async () => {
      await refetch();
    },
  };
};

export type LearningCoursePayloadState = {
  course?: LearningCourse;
  loading: boolean;
  error?: Error;
  reload: () => Promise<void>;
};

export const useLearningCoursePayload = (
  courseId: string | undefined,
  version: string | undefined
): LearningCoursePayloadState => {
  const { data, loading, error, refetch } = useQuery<LearningCoursePayloadQuery, LearningCoursePayloadQueryVariables>(
    LearningCoursePayloadDocument,
    {
      variables: { courseId: courseId ?? "", version: version ?? "" },
      skip: !courseId || !version,
      fetchPolicy: "cache-first",
    }
  );

  const reload = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  return useMemo(() => {
    if (error) return { loading, error, reload };
    if (!data) return { loading, reload };
    try {
      const payload = data.learningCoursePayload;
      const course = parseLearningCoursePayload(payload.payload);
      if (
        payload.courseId !== courseId ||
        payload.version !== version ||
        course.id !== courseId ||
        course.version !== version
      ) {
        throw new Error("Learning course payload identity mismatch");
      }
      return { course, loading, reload };
    } catch (parseError) {
      return {
        loading: false,
        error: parseError instanceof Error ? parseError : new Error("Invalid learning course payload"),
        reload,
      };
    }
  }, [courseId, data, error, loading, reload, version]);
};

export type MyLearningCourseState = {
  courseId: string;
  currentVersion: string;
  startedVersions: string[];
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";
  completedLessonIds: string[];
  bestScorePercent?: number | null;
  directAccess: boolean;
};

export type MyLearningState = {
  courses: MyLearningCourseState[];
  pathSources: Array<{
    assignmentId: string;
    pathId: string;
    pathVersion: string;
    orderedCourses: Array<{ courseId: string; available: boolean; locked: boolean }>;
  }>;
  loading: boolean;
  error?: ApolloError;
  reload: () => Promise<void>;
};

export const useMyLearning = (): MyLearningState => {
  const { data, loading, error, refetch } = useQuery<MyLearningQuery, MyLearningQueryVariables>(MyLearningDocument, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });
  return {
    courses: data?.myLearning.courses ?? [],
    pathSources: data?.myLearning.pathSources ?? [],
    loading,
    error,
    reload: async () => {
      await refetch();
    },
  };
};
